import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Calculator, DollarSign, Users, TrendingUp, Settings,
  PieChart, BarChart3, Save, Download, RefreshCw, Loader2,
  Brain, MessageSquare, Check, X, AlertCircle, Percent, Edit2
} from 'lucide-react';
import { toast } from 'sonner';

const CostCalculator = () => {
  // Cost parameters
  const [costs, setCosts] = useState({
    // Infrastructure costs (monthly)
    serverCost: 150,
    mongoCost: 50,
    cdnCost: 30,
    
    // AI costs per interaction
    gptCostPer1000Tokens: 0.002,
    gptOutputPer1000Tokens: 0.008,
    whisperPerMinute: 0.006,
    ttsPerCharacter: 0.000015,
    
    // Average usage per user per month
    avgChatMessages: 50,
    avgTokensPerMessage: 500,
    avgVoiceMinutes: 5,
    avgTTSCharacters: 2000,
    
    // Support & operations
    supportCostPerUser: 0.5,
    paymentProcessingPercent: 2.9,
    paymentFixedFee: 0.30,
  });

  // Pricing tiers - EDITABLE
  const [tiers, setTiers] = useState({
    // B2C Plans
    b2cFree: { price: 0, aiEnabled: false, limits: '10 tests/mes', name: 'Free' },
    b2cPremium: { price: 14.99, aiEnabled: true, limits: 'Ilimitado', name: 'Premium' },
    b2cPro: { price: 24.99, aiEnabled: true, limits: 'Ilimitado + Tutor avanzado', name: 'Pro' },
    
    // B2B Plans (per student per month)
    b2bBasic: { price: 8, aiEnabled: false, minStudents: 20, name: 'B2B Básico', volumeDiscounts: [
      { min: 50, discount: 10 },
      { min: 100, discount: 20 },
      { min: 200, discount: 30 },
    ]},
    b2bPremium: { price: 12, aiEnabled: true, minStudents: 20, name: 'B2B Premium', volumeDiscounts: [
      { min: 50, discount: 10 },
      { min: 100, discount: 20 },
      { min: 200, discount: 30 },
    ]},
    b2bEnterprise: { price: 19, aiEnabled: true, minStudents: 100, name: 'B2B Enterprise', volumeDiscounts: [
      { min: 200, discount: 15 },
      { min: 500, discount: 25 },
      { min: 1000, discount: 35 },
    ]},
  });

  // Edit mode for pricing
  const [editingTier, setEditingTier] = useState(null);
  const [editPrice, setEditPrice] = useState('');

  // Simulation parameters
  const [simulation, setSimulation] = useState({
    b2cTrialUsers: 500,      // 7 días prueba (no generan ingreso)
    b2cPremiumUsers: 200,
    b2cProUsers: 50,
    b2bInstitutions: 10,
    avgStudentsPerInstitution: 80,
    b2bPlanMix: { basic: 30, premium: 50, enterprise: 20 },
  });

  // Calculate cost per user by type
  const calculateCostPerUser = (aiEnabled) => {
    const baseInfraCostPerUser = 0.15; // ~$0.15/user for infrastructure
    
    if (!aiEnabled) {
      return baseInfraCostPerUser + costs.supportCostPerUser;
    }
    
    // AI user costs
    const chatCost = costs.avgChatMessages * costs.avgTokensPerMessage / 1000 * 
      (costs.gptCostPer1000Tokens + costs.gptOutputPer1000Tokens);
    const voiceCost = costs.avgVoiceMinutes * costs.whisperPerMinute;
    const ttsCost = costs.avgTTSCharacters * costs.ttsPerCharacter;
    
    return baseInfraCostPerUser + costs.supportCostPerUser + chatCost + voiceCost + ttsCost;
  };

  // Calculate margin for each plan
  const calculatePlanMargins = () => {
    const nonAICost = calculateCostPerUser(false);
    const aiCost = calculateCostPerUser(true);
    
    const margins = {};
    
    // B2C Plans
    if (tiers.b2cPremium.price > 0) {
      margins.b2cPremium = {
        price: tiers.b2cPremium.price,
        cost: aiCost,
        profit: tiers.b2cPremium.price - aiCost,
        margin: ((tiers.b2cPremium.price - aiCost) / tiers.b2cPremium.price * 100)
      };
    }
    
    if (tiers.b2cPro.price > 0) {
      const proCost = aiCost + 2; // Extra for advanced features
      margins.b2cPro = {
        price: tiers.b2cPro.price,
        cost: proCost,
        profit: tiers.b2cPro.price - proCost,
        margin: ((tiers.b2cPro.price - proCost) / tiers.b2cPro.price * 100)
      };
    }
    
    // B2B Plans
    margins.b2bBasic = {
      price: tiers.b2bBasic.price,
      cost: nonAICost,
      profit: tiers.b2bBasic.price - nonAICost,
      margin: ((tiers.b2bBasic.price - nonAICost) / tiers.b2bBasic.price * 100)
    };
    
    margins.b2bPremium = {
      price: tiers.b2bPremium.price,
      cost: aiCost,
      profit: tiers.b2bPremium.price - aiCost,
      margin: ((tiers.b2bPremium.price - aiCost) / tiers.b2bPremium.price * 100)
    };
    
    const enterpriseCost = aiCost + 1; // SLA costs
    margins.b2bEnterprise = {
      price: tiers.b2bEnterprise.price,
      cost: enterpriseCost,
      profit: tiers.b2bEnterprise.price - enterpriseCost,
      margin: ((tiers.b2bEnterprise.price - enterpriseCost) / tiers.b2bEnterprise.price * 100)
    };
    
    return margins;
  };

  // Calculate total metrics
  const calculateMetrics = () => {
    // Trial users have minimal cost but no revenue
    const trialInfraCost = simulation.b2cTrialUsers * 0.02; // Minimal cost for trial
    
    const totalPaidUsers = simulation.b2cPremiumUsers + 
      simulation.b2cProUsers + (simulation.b2bInstitutions * simulation.avgStudentsPerInstitution);
    
    const totalUsers = simulation.b2cTrialUsers + totalPaidUsers;
    
    const monthlyInfraCost = (
      (costs.serverCost * Math.ceil(totalPaidUsers / 1000)) +
      costs.mongoCost +
      costs.cdnCost +
      trialInfraCost
    );

    const aiUsers = simulation.b2cPremiumUsers + simulation.b2cProUsers + 
      (simulation.b2bInstitutions * simulation.avgStudentsPerInstitution * 
       (simulation.b2bPlanMix.premium + simulation.b2bPlanMix.enterprise) / 100);
    
    const monthlyAICost = aiUsers * (
      (costs.avgChatMessages * costs.avgTokensPerMessage / 1000 * 
       (costs.gptCostPer1000Tokens + costs.gptOutputPer1000Tokens)) +
      (costs.avgVoiceMinutes * costs.whisperPerMinute) +
      (costs.avgTTSCharacters * costs.ttsPerCharacter)
    );

    const supportCost = totalPaidUsers * costs.supportCostPerUser;
    const totalMonthlyCost = monthlyInfraCost + monthlyAICost + supportCost;

    // B2C Revenue (no free users)
    const b2cRevenue = 
      (simulation.b2cPremiumUsers * tiers.b2cPremium.price) +
      (simulation.b2cProUsers * tiers.b2cPro.price);

    // B2B Revenue
    const avgB2BStudents = simulation.avgStudentsPerInstitution;
    const getDiscountedPrice = (tier, students) => {
      const tierData = tiers[tier];
      let discount = 0;
      for (const d of tierData.volumeDiscounts) {
        if (students >= d.min) discount = d.discount;
      }
      return tierData.price * (1 - discount / 100);
    };

    const b2bRevenue = simulation.b2bInstitutions * avgB2BStudents * (
      (simulation.b2bPlanMix.basic / 100 * getDiscountedPrice('b2bBasic', avgB2BStudents)) +
      (simulation.b2bPlanMix.premium / 100 * getDiscountedPrice('b2bPremium', avgB2BStudents)) +
      (simulation.b2bPlanMix.enterprise / 100 * getDiscountedPrice('b2bEnterprise', avgB2BStudents))
    );

    const totalRevenue = b2cRevenue + b2bRevenue;

    const numTransactions = simulation.b2cPremiumUsers + simulation.b2cProUsers + simulation.b2bInstitutions;
    const paymentFees = (totalRevenue * costs.paymentProcessingPercent / 100) + 
      (numTransactions * costs.paymentFixedFee);

    const netRevenue = totalRevenue - paymentFees;
    const grossProfit = netRevenue - totalMonthlyCost;
    const marginPercent = (grossProfit / netRevenue) * 100;

    const costPerAIUser = (monthlyAICost / aiUsers) || 0;
    const costPerNonAIUser = (monthlyInfraCost / totalPaidUsers) + costs.supportCostPerUser;

    return {
      totalUsers,
      totalPaidUsers,
      aiUsers,
      monthlyInfraCost,
      monthlyAICost,
      supportCost,
      totalMonthlyCost,
      b2cRevenue,
      b2bRevenue,
      totalRevenue,
      paymentFees,
      netRevenue,
      grossProfit,
      marginPercent,
      costPerAIUser,
      costPerNonAIUser,
      annualProfit: grossProfit * 12,
    };
  };

  const metrics = calculateMetrics();
  const planMargins = calculatePlanMargins();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const MarginIndicator = ({ margin }) => {
    const color = margin >= 90 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : 
                  margin >= 80 ? 'text-amber-600 bg-amber-50 border-amber-200' : 
                  'text-red-600 bg-red-50 border-red-200';
    return (
      <span className={`${color} px-2 py-1 rounded-lg text-sm font-bold border`}>
        {margin.toFixed(1)}%
      </span>
    );
  };

  const handleEditPrice = (tierId) => {
    setEditingTier(tierId);
    setEditPrice(tiers[tierId].price.toString());
  };

  const handleSavePrice = (tierId) => {
    const newPrice = parseFloat(editPrice);
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error('Precio inválido');
      return;
    }
    setTiers(prev => ({
      ...prev,
      [tierId]: { ...prev[tierId], price: newPrice }
    }));
    setEditingTier(null);
    toast.success('Precio actualizado');
  };

  // Render plan card with edit capability
  const PlanCard = ({ tierId, planData, marginData, isB2B = false }) => {
    const isEditing = editingTier === tierId;
    
    return (
      <div className={`rounded-xl p-5 border-2 transition-all ${
        marginData.margin >= 90 ? 'border-emerald-200 bg-emerald-50/50' :
        marginData.margin >= 80 ? 'border-amber-200 bg-amber-50/50' :
        'border-red-200 bg-red-50/50'
      }`}>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-bold text-slate-900">{planData.name}</h4>
            <p className="text-xs text-slate-500">{planData.limits || (planData.aiEnabled ? 'Con IA' : 'Sin IA')}</p>
          </div>
          <MarginIndicator margin={marginData.margin} />
        </div>
        
        {/* Price Editor */}
        <div className="flex items-center gap-2 mb-4">
          {isEditing ? (
            <>
              <Input
                type="number"
                step="0.01"
                value={editPrice}
                onChange={(e) => setEditPrice(e.target.value)}
                className="w-24 text-lg font-bold"
                autoFocus
              />
              <span className="text-slate-500">{isB2B ? '/alum/mes' : '/mes'}</span>
              <Button size="sm" onClick={() => handleSavePrice(tierId)}>
                <Check className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setEditingTier(null)}>
                <X className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <>
              <span className="text-2xl font-black text-slate-900">{formatCurrency(planData.price)}</span>
              <span className="text-slate-500 text-sm">{isB2B ? '/alum/mes' : '/mes'}</span>
              <Button size="sm" variant="ghost" onClick={() => handleEditPrice(tierId)}>
                <Edit2 className="w-4 h-4" />
              </Button>
            </>
          )}
        </div>
        
        {/* Cost breakdown */}
        <div className="space-y-2 text-sm">
          <div className="flex justify-between text-slate-600">
            <span>Coste unitario:</span>
            <span className="font-medium">{formatCurrency(marginData.cost)}</span>
          </div>
          <div className="flex justify-between text-emerald-700 font-medium">
            <span>Beneficio unitario:</span>
            <span>{formatCurrency(marginData.profit)}</span>
          </div>
          <div className="pt-2 border-t">
            <div className="flex justify-between items-center">
              <span className="text-slate-600">Margen:</span>
              <span className={`text-lg font-bold ${
                marginData.margin >= 90 ? 'text-emerald-600' :
                marginData.margin >= 80 ? 'text-amber-600' : 'text-red-600'
              }`}>
                {marginData.margin.toFixed(1)}%
              </span>
            </div>
            {marginData.margin < 90 && (
              <p className="text-xs text-amber-600 mt-1">
                Objetivo: 90%+ | Sube precio a {formatCurrency(marginData.cost / 0.1)} para alcanzar 90%
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Calculator className="w-6 h-6 text-indigo-600" />
            Calculadora de Costes y Márgenes
          </h2>
          <p className="text-slate-600">Análisis de rentabilidad por plan (objetivo: 90-95% margen)</p>
        </div>
      </div>

      {/* Key Metrics Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
          <DollarSign className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{formatCurrency(metrics.totalRevenue)}</p>
          <p className="text-indigo-100 text-sm">Ingresos mensuales</p>
        </div>
        
        <div className="bg-gradient-to-br from-red-500 to-rose-600 rounded-xl p-5 text-white">
          <TrendingUp className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{formatCurrency(metrics.totalMonthlyCost)}</p>
          <p className="text-red-100 text-sm">Costes mensuales</p>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl p-5 text-white">
          <PieChart className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{formatCurrency(metrics.grossProfit)}</p>
          <p className="text-emerald-100 text-sm">Beneficio bruto</p>
        </div>
        
        <div className={`rounded-xl p-5 ${
          metrics.marginPercent >= 90 ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white' :
          metrics.marginPercent >= 80 ? 'bg-gradient-to-br from-amber-500 to-orange-600 text-white' :
          'bg-gradient-to-br from-red-500 to-rose-600 text-white'
        }`}>
          <Percent className="w-8 h-8 mb-2 opacity-80" />
          <p className="text-3xl font-bold">{metrics.marginPercent.toFixed(1)}%</p>
          <p className="text-sm opacity-80">Margen global</p>
        </div>
      </div>

      {/* Plan Margins - Editable */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-bold text-lg flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-indigo-600" />
            Márgenes por Plan (Editable)
          </h3>
          <p className="text-sm text-slate-500">Haz clic en el icono de editar para modificar precios</p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-8">
          {/* B2C Plans */}
          <div>
            <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <Users className="w-4 h-4" />
              B2C - Opositores Individuales
            </h4>
            <div className="space-y-4">
              {planMargins.b2cPremium && (
                <PlanCard tierId="b2cPremium" planData={tiers.b2cPremium} marginData={planMargins.b2cPremium} />
              )}
              {planMargins.b2cPro && (
                <PlanCard tierId="b2cPro" planData={tiers.b2cPro} marginData={planMargins.b2cPro} />
              )}
            </div>
          </div>

          {/* B2B Plans */}
          <div>
            <h4 className="font-semibold text-slate-700 mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              B2B - Instituciones (por alumno)
            </h4>
            <div className="space-y-4">
              <PlanCard tierId="b2bBasic" planData={tiers.b2bBasic} marginData={planMargins.b2bBasic} isB2B />
              <PlanCard tierId="b2bPremium" planData={tiers.b2bPremium} marginData={planMargins.b2bPremium} isB2B />
              <PlanCard tierId="b2bEnterprise" planData={tiers.b2bEnterprise} marginData={planMargins.b2bEnterprise} isB2B />
            </div>
          </div>
        </div>

        {/* Volume discounts note */}
        <div className="mt-6 p-4 bg-amber-50 rounded-lg border border-amber-200">
          <p className="text-sm font-medium text-amber-800">Descuentos por volumen B2B:</p>
          <p className="text-xs text-amber-600 mt-1">
            50+ alumnos: -10% | 100+ alumnos: -20% | 200+ alumnos: -30%
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Los márgenes mostrados son con precio base. Con descuentos el margen será menor.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* Cost Structure */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5 text-slate-500" />
            Estructura de Costes
          </h3>
          
          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium">Infraestructura</span>
                <span className="text-lg font-bold">{formatCurrency(metrics.monthlyInfraCost)}/mes</span>
              </div>
              <div className="text-sm text-slate-500 space-y-1">
                <div className="flex justify-between">
                  <span>Servidores ({Math.ceil(metrics.totalUsers/1000)} inst.)</span>
                  <span>{formatCurrency(costs.serverCost * Math.ceil(metrics.totalUsers/1000))}</span>
                </div>
                <div className="flex justify-between">
                  <span>MongoDB Atlas</span>
                  <span>{formatCurrency(costs.mongoCost)}</span>
                </div>
                <div className="flex justify-between">
                  <span>CDN</span>
                  <span>{formatCurrency(costs.cdnCost)}</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-indigo-50 rounded-lg">
              <div className="flex justify-between items-center mb-2">
                <span className="font-medium flex items-center gap-2">
                  <Brain className="w-4 h-4 text-indigo-600" />
                  Costes IA ({metrics.aiUsers.toLocaleString()} usuarios)
                </span>
                <span className="text-lg font-bold text-indigo-700">{formatCurrency(metrics.monthlyAICost)}/mes</span>
              </div>
              <div className="text-sm text-indigo-600 space-y-1">
                <div className="flex justify-between">
                  <span>Coste por usuario IA</span>
                  <span>{metrics.costPerAIUser.toFixed(2)}€/mes</span>
                </div>
              </div>
            </div>

            <div className="p-4 bg-amber-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">Soporte + Ops</span>
                <span className="text-lg font-bold text-amber-700">{formatCurrency(metrics.supportCost)}/mes</span>
              </div>
            </div>

            <div className="p-4 bg-rose-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="font-medium">Comisiones pago</span>
                <span className="text-lg font-bold text-rose-700">{formatCurrency(metrics.paymentFees)}/mes</span>
              </div>
            </div>
          </div>
        </div>

        {/* Simulation */}
        <div className="bg-white rounded-xl border p-6">
          <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-slate-500" />
            Simulación de Volumen
          </h3>

          <div className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium mb-3">B2C (Sin plan gratuito - Solo 7 días prueba)</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-slate-500">En prueba (7 días)</label>
                  <Input
                    type="number"
                    value={simulation.b2cTrialUsers}
                    onChange={(e) => setSimulation({...simulation, b2cTrialUsers: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                  <p className="text-xs text-slate-400 mt-1">No generan ingreso</p>
                </div>
                <div>
                  <label className="text-xs text-slate-500">Premium</label>
                  <Input
                    type="number"
                    value={simulation.b2cPremiumUsers}
                    onChange={(e) => setSimulation({...simulation, b2cPremiumUsers: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Pro</label>
                  <Input
                    type="number"
                    value={simulation.b2cProUsers}
                    onChange={(e) => setSimulation({...simulation, b2cProUsers: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t flex justify-between">
                <span className="text-sm text-slate-600">Ingresos B2C</span>
                <span className="font-bold text-indigo-600">{formatCurrency(metrics.b2cRevenue)}/mes</span>
              </div>
            </div>

            <div className="p-4 bg-indigo-50 rounded-lg">
              <h4 className="font-medium mb-3">B2B</h4>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="text-xs text-slate-500">Instituciones</label>
                  <Input
                    type="number"
                    value={simulation.b2bInstitutions}
                    onChange={(e) => setSimulation({...simulation, b2bInstitutions: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500">Alumnos/inst.</label>
                  <Input
                    type="number"
                    value={simulation.avgStudentsPerInstitution}
                    onChange={(e) => setSimulation({...simulation, avgStudentsPerInstitution: parseInt(e.target.value) || 0})}
                    className="mt-1"
                  />
                </div>
              </div>
              <div className="mt-3 pt-3 border-t flex justify-between">
                <span className="text-sm text-indigo-600">Ingresos B2B</span>
                <span className="font-bold text-indigo-700">{formatCurrency(metrics.b2bRevenue)}/mes</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Annual Projection */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Proyección Anual</h3>
            <p className="text-indigo-200">Basada en la simulación actual</p>
          </div>
          <div className="text-right">
            <p className="text-4xl font-black">{formatCurrency(metrics.annualProfit)}</p>
            <p className="text-indigo-200">Beneficio bruto anual</p>
          </div>
        </div>
        <div className="grid grid-cols-4 gap-4 mt-6 pt-6 border-t border-white/20">
          <div>
            <p className="text-indigo-200 text-sm">Ingresos anuales</p>
            <p className="text-xl font-bold">{formatCurrency(metrics.totalRevenue * 12)}</p>
          </div>
          <div>
            <p className="text-indigo-200 text-sm">Costes anuales</p>
            <p className="text-xl font-bold">{formatCurrency(metrics.totalMonthlyCost * 12)}</p>
          </div>
          <div>
            <p className="text-indigo-200 text-sm">Total usuarios</p>
            <p className="text-xl font-bold">{metrics.totalUsers.toLocaleString()}</p>
          </div>
          <div>
            <p className="text-indigo-200 text-sm">ARPU (B2C paid)</p>
            <p className="text-xl font-bold">
              {formatCurrency(metrics.b2cRevenue / (simulation.b2cPremiumUsers + simulation.b2cProUsers || 1))}
            </p>
          </div>
        </div>
      </div>

      {/* B2B AI Credit Packages for Resale */}
      <div className="bg-white rounded-xl border p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="font-bold text-lg flex items-center gap-2">
              <Brain className="w-5 h-5 text-purple-600" />
              Paquetes de Sesiones IA para Reventa B2B
            </h3>
            <p className="text-sm text-slate-500">Vende sesiones de Tutor IA a instituciones con alto margen</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-slate-500">Coste real por sesión</p>
            <p className="text-lg font-bold text-purple-600">~0,27€</p>
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { name: 'Starter', sessions: 500, price: 399, students: '~25 alumnos/mes' },
            { name: 'Growth', sessions: 2000, price: 1299, students: '~100 alumnos/mes' },
            { name: 'Pro', sessions: 5000, price: 2999, students: '~250 alumnos/mes' },
            { name: 'Enterprise', sessions: 15000, price: 7999, students: '~750 alumnos/mes' },
          ].map((pkg, i) => {
            const cost = pkg.sessions * 0.27;
            const profit = pkg.price - cost;
            const margin = (profit / pkg.price * 100);
            
            return (
              <div key={i} className={`rounded-xl p-4 border-2 ${
                margin >= 60 ? 'border-emerald-200 bg-emerald-50/50' : 
                margin >= 40 ? 'border-amber-200 bg-amber-50/50' :
                'border-red-200 bg-red-50/50'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-bold text-slate-900">{pkg.name}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${
                    margin >= 60 ? 'bg-emerald-100 text-emerald-700' : 
                    margin >= 40 ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {margin.toFixed(0)}% margen
                  </span>
                </div>
                
                <p className="text-2xl font-black text-slate-900">{formatCurrency(pkg.price)}</p>
                <p className="text-xs text-slate-500 mb-3">{pkg.sessions.toLocaleString()} sesiones</p>
                
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Tu coste:</span>
                    <span>{formatCurrency(cost)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Tu ganancia:</span>
                    <span>{formatCurrency(profit)}</span>
                  </div>
                </div>
                
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <p className="text-xs text-purple-600 font-medium">{pkg.students}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
          <p className="text-sm font-medium text-purple-800">💡 Modelo de negocio:</p>
          <ul className="text-xs text-purple-600 mt-2 space-y-1">
            <li>• Las academias compran paquetes de sesiones IA al por mayor</li>
            <li>• Distribuyen las sesiones a sus alumnos según necesiten</li>
            <li>• Cuando se agotan, compran más o el alumno hace upsell directamente</li>
            <li>• Márgenes del 60-66% garantizados en todos los paquetes</li>
          </ul>
        </div>
      </div>

      {/* B2C Credit Packages */}
      <div className="bg-white rounded-xl border p-6">
        <h3 className="font-bold text-lg mb-4 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-indigo-600" />
          Paquetes de Créditos IA para Usuarios B2C
        </h3>
        
        <div className="grid md:grid-cols-4 gap-4">
          {[
            { name: 'Starter', sessions: 10, price: 4.99, popular: false },
            { name: 'Popular', sessions: 50, price: 19.99, popular: true },
            { name: 'Pro', sessions: 150, price: 49.99, popular: false },
            { name: 'Ilimitado', sessions: -1, price: 29.99, popular: false, subscription: true },
          ].map((pkg, i) => {
            const cost = pkg.sessions > 0 ? pkg.sessions * 0.27 : 10; // Estimate for unlimited
            const profit = pkg.price - cost;
            const margin = pkg.sessions > 0 ? (profit / pkg.price * 100) : 66;
            
            return (
              <div key={i} className={`rounded-xl p-4 border-2 relative ${
                pkg.popular ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200'
              }`}>
                {pkg.popular && (
                  <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-indigo-600 text-white text-xs px-2 py-0.5 rounded-full">
                    Más vendido
                  </span>
                )}
                <h4 className="font-bold text-slate-900">{pkg.name}</h4>
                <p className="text-2xl font-black text-slate-900 mt-1">{formatCurrency(pkg.price)}</p>
                <p className="text-xs text-slate-500">
                  {pkg.sessions > 0 ? `${pkg.sessions} sesiones` : 'Sesiones ilimitadas/mes'}
                </p>
                
                <div className="mt-3 pt-3 border-t text-sm">
                  <div className="flex justify-between text-slate-600">
                    <span>Margen:</span>
                    <span className={margin >= 40 ? 'text-emerald-600 font-bold' : 'text-amber-600'}>
                      ~{margin.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CostCalculator;
